import type {AttachmentsContext} from '@github-ui/attachments'
import type {FileAttachment} from '@github-ui/attachments/types'
import {sendEvent} from '@github-ui/hydro-analytics'

import type {CopilotAutocompleteManager} from './copilot-autocompletions'
import type {CopilotChatManager} from './copilot-chat-manager'
import type {CopilotChatState} from './copilot-chat-reducer'
import type {CopilotChatModel} from './copilot-chat-types'
import {copilotFeatureFlags} from './copilot-feature-flags'
import {Base64FileAttachment, UploadableFileAttachment} from './uploadable-file-attachment'

// Should be limited to images that we know we can base64 encode and are supported by our models.
// We pull the types from the model capabilities and filter for these types.
const ACCEPTED_BASE64_IMAGE_FILE_MIME_TYPES: string[] = ['image/gif', 'image/jpeg', 'image/png', 'image/webp']

const DOTCOM_CHAT_ATTACHMENT_LIMIT = 1
const DOTCOM_CHAT_ATTACHMENT_SIZE_LIMIT = 10 * 1024 * 1024 // 10MB
const DOTCOM_CHAT_ATTACHMENT_SIZE_LIMIT_BASE64 = 500 * 1024 // 500KB

export class CopilotImageAttacher {
  private state: CopilotChatState
  private manager: CopilotChatManager
  private autocomplete: CopilotAutocompleteManager

  public constructor(state: CopilotChatState, manager: CopilotChatManager, autocomplete: CopilotAutocompleteManager) {
    this.state = state
    this.manager = manager
    this.autocomplete = autocomplete
  }

  public static getAllowedImageFileExtensions(model: CopilotChatModel): string {
    return this.getFileExtensions(model).join(',')
  }

  public static isTypeAllowed(itemType: string, model: CopilotChatModel): boolean {
    itemType = itemType.toLowerCase()
    const mimeTypes = this.getMimeTypes(model)
    return mimeTypes.includes(itemType)
  }

  public static getAttachmentLimit(): number {
    return DOTCOM_CHAT_ATTACHMENT_LIMIT
  }

  public static getAttachmentSizeLimit(): number {
    if (copilotFeatureFlags.dotcomChatFileUpload) {
      return DOTCOM_CHAT_ATTACHMENT_SIZE_LIMIT
    }
    return DOTCOM_CHAT_ATTACHMENT_SIZE_LIMIT_BASE64
  }

  private static getFileExtensions(model: CopilotChatModel): string[] {
    const extensions = this.getMimeTypes(model).map(type => `.${type.split('/')[1]}`)

    if (extensions.includes('.jpeg')) {
      extensions.push('.jpg')
    }

    return extensions
  }

  private static getMimeTypes(model: CopilotChatModel): string[] {
    if (model.capabilities.limits.vision) {
      const supportedTypes = model.capabilities.limits.vision.supported_media_types
      // for now, we can only return supported types that can be base 64 encoded. Remove once we stop
      // encoding images as base 64
      return supportedTypes.filter(type => ACCEPTED_BASE64_IMAGE_FILE_MIME_TYPES.includes(type))
    }

    return []
  }

  public async addImageAttachment(
    file: File,
    attachmentsContext: AttachmentsContext,
    signal: AbortSignal,
  ): Promise<void> {
    return new Promise<void>((resolve, reject) => {
      const [imageState, imageApi] = attachmentsContext

      // error here if there is already one image uploaded
      const hasImageAttachment = imageState.attachments.length >= DOTCOM_CHAT_ATTACHMENT_LIMIT
      if (hasImageAttachment) {
        const errorMessage = 'Only one image can be uploaded at a time'
        this.manager.addAmbientError(errorMessage)
        sendEvent('dotcom_chat.vision.error', {type: 'file_limit_reached'})
        reject(new Error(errorMessage))
        return
      }

      let attachment: FileAttachment
      if (copilotFeatureFlags.dotcomChatFileUpload) {
        attachment = new UploadableFileAttachment(file, signal)
      }
      attachment ||= new Base64FileAttachment(file)

      imageApi.addFiles([attachment])
      this.manager.dispatch({type: 'IMAGE_UPLOADING'})
      resolve()
    })
  }
}
